import React, { useState, useEffect, useRef, useMemo } from 'react';

// Fixed score range: 300-850
const SCORE_MIN = 300;
const SCORE_MAX = 850;

/**
 * Normalize a credit score to 0-1 range
 * @param {number} score - Raw score value
 * @param {number} minScore - Minimum score (default 300)
 * @param {number} maxScore - Maximum score (default 850)
 * @returns {number} Normalized value between 0 and 1
 */
const normalizeScore = (score, minScore = SCORE_MIN, maxScore = SCORE_MAX) => {
  // Handle invalid/null/undefined scores
  if (score === null || score === undefined || isNaN(score)) return 0;
  const numScore = Number(score);
  if (isNaN(numScore)) return 0;
  // Clamp and normalize
  const clamped = Math.max(minScore, Math.min(maxScore, numScore));
  return (clamped - minScore) / (maxScore - minScore);
};

/**
 * Premium Score Gauge - Semi-circle gauge with animated marker
 * 
 * Features:
 * - SVG viewBox: 0 0 170 124
 * - Center: cx=85, cy=94, radius=62
 * - Gradient arc from red to green
 * - Marker follows arc path based on normalized score
 * - Score/pill rendering handled by parent
 * 
 * @param {number} score - Current score value (300-850)
 * @param {number} previousScore - Starting score for animation (default: SCORE_MIN)
 * @param {number} size - Width of the gauge (default 170)
 * @param {number} height - Height of the gauge (default 124)
 * @param {boolean} animate - Whether to animate the transition (default true)
 * @param {object} theme - Theme object with isDark, baseStroke properties
 * @param {function} onScoreUpdate - Callback for animated score updates
 */
export const ScoreGauge = ({ 
  score, 
  previousScore = SCORE_MIN, 
  size = 170,
  height = 124,
  animate = true, 
  theme,
  onScoreUpdate
}) => {
  // === GEOMETRY (matching reference HTML) ===
  const cx = 85;
  const cy = 94;
  const r = 62;
  const strokeWidth = 8;
  
  // === NORMALIZE SCORES ===
  const targetProgress = normalizeScore(score);
  const startProgress = normalizeScore(previousScore);
  
  // State for animated progress - KEY FIX: use ref to track last score
  const [progress, setProgress] = useState(animate ? startProgress : targetProgress);
  const lastScoreRef = useRef(score);
  const animationFrameRef = useRef(null);
  
  // Refs for callbacks
  const callbackRef = useRef(onScoreUpdate);
  callbackRef.current = onScoreUpdate;
  
  // === ANIMATION EFFECT ===
  // This effect runs whenever score changes and handles animation
  useEffect(() => {
    // Skip if score is invalid
    if (score === null || score === undefined || isNaN(Number(score))) {
      setProgress(0);
      return;
    }
    
    const numScore = Number(score);
    const newTargetProgress = normalizeScore(numScore);
    
    // If not animating, set progress directly
    if (!animate) {
      setProgress(newTargetProgress);
      if (callbackRef.current) callbackRef.current(numScore);
      lastScoreRef.current = score;
      return;
    }
    
    // Determine animation start point
    // If this is a score CHANGE (not initial mount), animate from current progress
    // If it's initial mount, animate from startProgress
    const isScoreChange = lastScoreRef.current !== score && lastScoreRef.current !== undefined;
    const animStartProgress = isScoreChange ? progress : startProgress;
    
    // Skip animation if we're already at target
    if (Math.abs(animStartProgress - newTargetProgress) < 0.001) {
      setProgress(newTargetProgress);
      if (callbackRef.current) callbackRef.current(numScore);
      lastScoreRef.current = score;
      return;
    }
    
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    const duration = 1300;
    let startTime = null;
    
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    
    const tick = (now) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const currentProg = animStartProgress + (newTargetProgress - animStartProgress) * eased;
      
      setProgress(currentProg);
      
      if (callbackRef.current) {
        const animScore = Math.round(SCORE_MIN + currentProg * (SCORE_MAX - SCORE_MIN));
        callbackRef.current(animScore);
      }
      
      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(tick);
      } else {
        setProgress(newTargetProgress);
        if (callbackRef.current) callbackRef.current(numScore);
        animationFrameRef.current = null;
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(tick);
    lastScoreRef.current = score;
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [score, animate, startProgress]); // Removed 'progress' from deps to avoid infinite loop
  
  // === ARC PATH ===
  const arcPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const totalLength = Math.PI * r; // Semi-circle arc length
  
  // === MARKER POSITION (using polar coordinates) ===
  // Progress 0 = left end (180°), Progress 1 = right end (0°)
  // Angle goes from PI (180°) to 0 (0°) as progress goes 0 to 1
  const theta = Math.PI * (1 - progress);
  const markerX = cx + r * Math.cos(theta);
  const markerY = cy - r * Math.sin(theta);
  
  // Marker color based on progress
  const getMarkerColor = () => {
    if (progress < 0.30) return '#EF4444'; // Red - Poor
    if (progress < 0.55) return '#FBBF24'; // Yellow - Fair
    if (progress < 0.75) return '#CBEA2D'; // Light green - Good
    return '#10B981'; // Green - Excellent
  };
  
  const uid = useMemo(() => `gauge-${Math.random().toString(36).slice(2, 8)}`, []);
  
  return (
    <svg 
      width={size} 
      height={height} 
      viewBox="0 0 170 124"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        {/* Gradient matching reference HTML exactly */}
        <linearGradient id={`grad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#EF4444" />
          <stop offset="38%" stopColor="#F59E0B" />
          <stop offset="65%" stopColor="#CBEA2D" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
      </defs>
      
      {/* Base arc (background) */}
      <path
        d={arcPath}
        fill="none"
        stroke={theme?.baseStroke || 'rgba(189,198,211,0.42)'}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      
      {/* Active arc (gradient fill) - shows progress */}
      <path
        d={arcPath}
        fill="none"
        stroke={`url(#grad-${uid})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${totalLength * progress} ${totalLength}`}
        strokeDashoffset="0"
      />
      
      {/* Marker group - positioned along the arc based on progress */}
      <g transform={`translate(${markerX}, ${markerY})`}>
        {/* White outer ring */}
        <circle r="7.2" fill="#FFFFFF" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }} />
        {/* Colored inner fill */}
        <circle r="4.2" fill={getMarkerColor()} />
        {/* White highlight */}
        <circle r="1.8" fill="#FFFFFF" />
      </g>
    </svg>
  );
};

// Export constants for reuse
export { SCORE_MIN, SCORE_MAX, normalizeScore };

export default ScoreGauge;
